import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  getCallScribeAccess,
  getCallScribeCall,
  getCallScribeCallsByContact,
  getCallScribeCallsByTicket,
  getCallScribeTranscript,
  getCallScribeVoiceToken,
  initiateCallScribeCall,
  postCallScribeTimeEntry,
  rejectCallScribeSuggestion,
  stopCallScribeRecording,
} from 'services/callScribe';
import { extractApiErrorMessage } from 'services/callScribe/apiError';
import { getCallScribeMyDashboard } from 'services/callScribe/dashboard';
import {
  ICallScribeTimeEntryRequest,
  IInitiateCallRequest,
} from 'services/callScribe/types';

/** Loads the per-tenant CallScribe access info. Called on Pod mount for sidebar gating. */
export const ThunkGetCallScribeAccess = createAsyncThunk(
  'callScribe/getAccess',
  () => getCallScribeAccess().then(res => res.data),
);

/** Loads the per-tech dashboard projection (Today / Recent / Status / Sentiment / Top / QuickDial / FollowUp). */
export const ThunkGetCallScribeMyDashboard = createAsyncThunk(
  'callScribe/getMyDashboard',
  async () => {
    const res = await getCallScribeMyDashboard();
    if (!res) {
      throw new Error('Failed to load CallScribe dashboard');
    }
    return res.data;
  },
);

/**
 * Mints a fresh Twilio Voice JWT. Returned token is consumed by useTwilioDevice.
 *
 * Note: the global axios response interceptor swallows rejected responses and resolves
 * with `undefined`, so we must guard against `res` being undefined here — otherwise the
 * destructuring throws a misleading "Cannot read properties of undefined (reading 'data')"
 * which surfaces in the softphone error banner instead of the real BE message.
 *
 * We also pass `meta: { silent: true }` semantically by setting a flag on the request config
 * so the global axios interceptor can skip its snackbar for this specific call — the BE returns
 * 503 in fully-expected dev environments where Twilio API Key + TwiML App SID are unset, and
 * we don't want to spam the user with a red toast every Pod mount.
 */
export const ThunkGetCallScribeVoiceToken = createAsyncThunk(
  'callScribe/getVoiceToken',
  async () => {
    const res = await getCallScribeVoiceToken();
    if (!res) {
      throw new Error('Softphone unavailable');
    }
    return res.data;
  },
);

/** Loads the call log for the currently-open PSA ticket. */
export const ThunkGetCallScribeCallsByTicket = createAsyncThunk(
  'callScribe/getCallsByTicket',
  (args: { crmSourceId: number; ticketId: string }) =>
    getCallScribeCallsByTicket(args.crmSourceId, args.ticketId).then(res => res.data),
);

export const ThunkGetCallScribeCallsByContact = createAsyncThunk(
  'callScribe/getCallsByContact',
  (args: { crmSourceId: number; contactId: string; companyId?: string | null }) =>
    getCallScribeCallsByContact(args.crmSourceId, args.contactId, args.companyId).then(
      res => res.data,
    ),
);

/**
 * Loads a single call's projection. Used by the popup softphone to poll for the
 * post-call intelligence row after hangup. The global axios interceptor swallows
 * non-2xx responses to `undefined`, which the caller treats as "keep polling".
 */
export const ThunkGetCallScribeCall = createAsyncThunk(
  'callScribe/getCall',
  async (callId: number) => {
    const res = await getCallScribeCall(callId);
    if (!res) {
      return null;
    }
    return res.data;
  },
);

/** Initiates an outbound call (creates the row + dials Twilio). */
export const ThunkInitiateCallScribeCall = createAsyncThunk(
  'callScribe/initiateCall',
  async (body: IInitiateCallRequest, { rejectWithValue }) => {
    try {
      const res = await initiateCallScribeCall(body);
      return res.data;
    } catch (err) {
      return rejectWithValue(
        extractApiErrorMessage(err, 'The call could not be started. Please try again.'),
      );
    }
  },
);

/** Posts the AI-suggested (or tech-edited) time entry for a completed call. */
export const ThunkPostCallScribeTimeEntry = createAsyncThunk(
  'callScribe/postTimeEntry',
  async (
    args: { callId: number; body: ICallScribeTimeEntryRequest },
    { rejectWithValue },
  ) => {
    try {
      const res = await postCallScribeTimeEntry(args.callId, args.body);
      return res.data;
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      return rejectWithValue({
        status,
        message: extractApiErrorMessage(
          err,
          'The time entry could not be posted. Please try again.',
        ),
      });
    }
  },
);

export const ThunkStopCallScribeRecording = createAsyncThunk(
  'callScribe/stopRecording',
  (callId: number) => stopCallScribeRecording(callId).then(res => res.data),
);

/** Records a tech rejection of the AI suggestion (no PSA write). */
export const ThunkRejectCallScribeSuggestion = createAsyncThunk(
  'callScribe/rejectSuggestion',
  (callId: number) => rejectCallScribeSuggestion(callId).then(res => res.data),
);

/**
 * Fetches the most-recent transcript for a single call. The BE returns 404 while
 * transcription is still running, which the global axios interceptor surfaces as a
 * resolved-undefined response — we treat that as "not ready yet" and return null so
 * the slice can keep the row in a "still processing" state without throwing.
 */
export const ThunkGetCallScribeTranscript = createAsyncThunk(
  'callScribe/getTranscript',
  async (callId: number) => {
    const res = await getCallScribeTranscript(callId);
    if (!res || res.status === 202) {
      return null;
    }
    return res.data;
  },
);
